import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ALL_LLM_USER_BUDGETS, LIST_LLM_PRICING, LLM_COST_BREAKDOWN, LLM_USER_BUDGET,
} from '../queries/llm';
import {
  DELETE_LLM_PRICING, SEED_LLM_PRICING_FROM_DEFAULTS, SET_LLM_USER_BUDGET, UPSERT_LLM_PRICING,
} from '../mutations/llm';

/** The windows the cost screens offer, in hours — the meter is hour-based. */
export const COST_WINDOWS = [
  ['24h', 24], ['7 days', 168], ['30 days', 720], ['90 days', 2160],
];
export const DEFAULT_COST_WINDOW = 168;

/** Every dimension the meter can group by, and what to call it on screen. */
export const COST_DIMENSIONS = {
  day: 'Day',
  agent_name: 'Agent',
  user_id: 'User',
  model_id: 'Model',
  provider: 'Provider',
  thread_id: 'Thread',
  session_id: 'Session',
};

/**
 * The window that safely covers one calendar day.
 *
 * The meter takes a LOOKBACK (`sinceHours`) and nothing else — there is no
 * date range on the API. So asking for one day means asking for a window that
 * reaches back past it and picking that day out of the answer. The extra 48
 * hours is slack for the server's clock and for late-arriving rows.
 */
export const daySinceHours = (day) => {
  const start = new Date(`${day}T00:00:00`).getTime();
  if (Number.isNaN(start)) return 48;
  return Math.max(48, Math.ceil((Date.now() - start) / 3600000) + 48);
};

/**
 * One roll-up of agent spend.
 *
 * `groupBy` is a list because the backend will cross two dimensions if asked;
 * every screen here asks for one. An empty list returns the single total row,
 * which is how the headline figures are read.
 *
 * `day` (YYYY-MM-DD) narrows to a single date. Since the API has no date
 * range, that is done by crossing the requested dimension with `day`, asking
 * for a window that covers the date, and keeping only that date's rows — so
 * the figures are still the server's, just one day's worth. The limit is
 * raised in that mode because one row per dimension per day is many more rows.
 */
export function useLlmCosts(groupBy, {
  sinceHours = DEFAULT_COST_WINDOW, limit = 50, skip = false, day = null, ...filters
} = {}) {
  /* In day mode the server is asked for the cross, and `day` is picked out of
     the answer below. A `day` already in groupBy is not added twice. */
  const dayMode = Boolean(day);
  const askedGroupBy = dayMode && !groupBy.includes('day') ? [...groupBy, 'day'] : groupBy;

  const { data, loading, error, refetch } = useQuery(LLM_COST_BREAKDOWN, {
    variables: {
      groupBy: askedGroupBy,
      sinceHours: dayMode ? daySinceHours(day) : sinceHours,
      limit: dayMode ? Math.max(limit, 3000) : limit,
      ...filters,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const raw = data?.llmCostBreakdown ?? [];
    const dayOf = (r) => (r.bucket ?? []).find((b) => b.name === 'day')?.value?.slice(0, 10);
    const kept = dayMode ? raw.filter((r) => dayOf(r) === day) : raw;

    const rows = kept.map((r) => ({
      /* One bucket per grouped dimension; a total row has none. In day mode the
         day bucket is dropped from the key — every row is that day, so
         repeating it would only make the labels longer. */
      key: (r.bucket ?? [])
        .filter((b) => !dayMode || b.name !== 'day')
        .map((b) => b.value ?? '(none)').join(' · ') || 'All',
      bucket: r.bucket ?? [],
      calls: Number(r.calls) || 0,
      inputTokens: Number(r.inputTokens) || 0,
      outputTokens: Number(r.outputTokens) || 0,
      cacheReadTokens: Number(r.cacheReadTokens) || 0,
      cacheWriteTokens: Number(r.cacheWriteTokens) || 0,
      reasoningTokens: Number(r.reasoningTokens) || 0,
      cost: Number(r.totalCostUsd) || 0,
    })).sort((a, b) => b.cost - a.cost);
    const total = rows.reduce((t, r) => ({
      calls: t.calls + r.calls,
      inputTokens: t.inputTokens + r.inputTokens,
      outputTokens: t.outputTokens + r.outputTokens,
      cacheReadTokens: t.cacheReadTokens + r.cacheReadTokens,
      cacheWriteTokens: t.cacheWriteTokens + r.cacheWriteTokens,
      reasoningTokens: t.reasoningTokens + r.reasoningTokens,
      cost: t.cost + r.cost,
    }), {
      calls: 0, inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0, reasoningTokens: 0, cost: 0,
    });
    return { rows, total, loading, error, refetch };
  }, [data, loading, error, refetch, dayMode, day]);
}

/** Every account with a budget. `onlyOverBudget` is the desk's working view. */
export function useLlmBudgets({ sortBy = 'monthly_spend', onlyOverBudget = false, limit = 50, offset = 0 } = {}) {
  const { data, loading, error, refetch } = useQuery(ALL_LLM_USER_BUDGETS, {
    variables: { sortBy, onlyOverBudget, limit, offset },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.allLlmUserBudgets ?? [],
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/** One account's caps, for the drill-in. */
export function useLlmUserBudget(userId, { skip = false } = {}) {
  const { data, loading, error } = useQuery(LLM_USER_BUDGET, {
    variables: { userId: String(userId ?? '') },
    skip: skip || !userId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return { budget: data?.llmUserBudget ?? null, loading, error };
}

export const useSetLlmBudget = () => useMutation(SET_LLM_USER_BUDGET, {
  refetchQueries: ['AllLlmUserBudgets', 'LlmUserBudget'],
});

/* ── what the models cost ───────────────────────────────────────────────── */

/**
 * The rate card behind every figure on this screen.
 *
 * Shown with inactive rows included by default: a model whose pricing was
 * deactivated still has historical calls priced by it, so hiding it makes the
 * old totals unexplainable.
 */
export function useLlmPricing({ onlyActive = null } = {}) {
  const { data, loading, error, refetch } = useQuery(LIST_LLM_PRICING, {
    variables: { onlyActive },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.listLlmPricing ?? [],
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

export const useUpsertLlmPricing = () => useMutation(UPSERT_LLM_PRICING, {
  refetchQueries: ['ListLlmPricing'],
});
export const useDeleteLlmPricing = () => useMutation(DELETE_LLM_PRICING, {
  refetchQueries: ['ListLlmPricing'],
});
export const useSeedLlmPricing = () => useMutation(SEED_LLM_PRICING_FROM_DEFAULTS, {
  refetchQueries: ['ListLlmPricing'],
});
